import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, KeyRound, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { authApi } from '../lib/api';
import { PRODUCT_NAME, BRAND_LOGO_GLOW } from '../lib/version';
import usePageTitle from '../hooks/usePageTitle';

/*
 * Set a new password from a Supabase recovery link. A STANDALONE page (no Layout, no sidebar) -
 * the sibling of LoginPage, so it shares its composition: the reaper glow + product name above a
 * centred `.surface` card, the same input classes, one glowing primary action.
 *
 * The recovery token arrives in the URL hash (#access_token=...&type=recovery); LoginPage also
 * stashes it in session/localStorage before redirecting here, so the token is looked for in all
 * three places and removed from storage once read. That logic is unchanged.
 */

/* Shared frame for every state: brand mark, then the card. `inputCls` mirrors LoginPage. */
const inputCls = 'h-11 bg-secondary/60 border-border text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-ring/60';

function AuthFrame({ appName, children, testId }) {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground px-5 py-10 sm:px-8 safe-top safe-bottom">
      <div className="w-full max-w-md animate-fade-up" data-testid={testId}>
        <div className="mb-8 flex flex-col items-center text-center">
          <img src={BRAND_LOGO_GLOW} alt="" className="h-20 w-20 drop-shadow-[0_12px_30px_hsl(var(--primary)/.4)]" />
          <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight">{appName}</h1>
        </div>
        <div className="surface p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [error, setError] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const { branding } = useBranding();
  usePageTitle('Reset password');

  const appName = branding?.appName || PRODUCT_NAME;

  useEffect(() => {
    const findToken = () => {
      console.log('[ResetPassword] Looking for token...');
      console.log('[ResetPassword] Current URL:', window.location.href);
      console.log('[ResetPassword] Hash:', window.location.hash);

      // Method 1: Check current URL hash
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        const params = new URLSearchParams(hash.substring(1));
        const token = params.get('access_token');
        if (token) {
          console.log('[ResetPassword] Found token in URL hash');
          return token;
        }
      }

      // Method 2: Check sessionStorage
      const sessionToken = sessionStorage.getItem('supabase_recovery_token');
      if (sessionToken) {
        console.log('[ResetPassword] Found token in sessionStorage');
        sessionStorage.removeItem('supabase_recovery_token');
        return sessionToken;
      }

      // Method 3: Check localStorage
      const localToken = localStorage.getItem('supabase_recovery_token');
      if (localToken) {
        console.log('[ResetPassword] Found token in localStorage');
        localStorage.removeItem('supabase_recovery_token');
        return localToken;
      }

      console.log('[ResetPassword] No token found');
      return null;
    };

    const token = findToken();
    setIsChecking(false);

    if (token) {
      setAccessToken(token);
    } else {
      setError('Invalid or expired password reset link. Please request a new one.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Use the new dedicated recovery endpoint
      await authApi.updatePasswordRecovery(accessToken, password);

      setIsSuccess(true);
      toast.success('Password updated successfully!');

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      const message = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      toast.error(message);

      if (message.includes('expired') || message.includes('invalid') || message.includes('credentials')) {
        setError('Your reset link has expired. Please request a new one.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isChecking) {
    return (
      <AuthFrame appName={appName} testId="reset-password-checking">
        <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
          <span className="sr-only">Checking your reset link…</span>
        </div>
      </AuthFrame>
    );
  }

  // Error state
  if (error) {
    return (
      <AuthFrame appName={appName} testId="reset-password-error">
        <div className="text-center" role="alert">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
            <AlertCircle className="h-6 w-6" />
          </span>
          <h2 className="mt-4 font-heading text-xl font-semibold">Reset Link Invalid</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
            {error}
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full h-11 mt-6 font-semibold shadow-glow"
            data-testid="reset-password-back-to-login"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Button>
        </div>
      </AuthFrame>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <AuthFrame appName={appName} testId="reset-password-success">
        <div className="text-center" role="status" aria-live="polite">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success ring-1 ring-success/20">
            <CheckCircle className="h-6 w-6" />
          </span>
          <h2 className="mt-4 font-heading text-xl font-semibold">Password Updated!</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
            Your password has been successfully reset. Redirecting to login...
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full h-11 mt-6 font-semibold shadow-glow"
            data-testid="reset-password-go-to-login"
          >
            Go to Login
          </Button>
        </div>
      </AuthFrame>
    );
  }

  // Reset form
  return (
    <AuthFrame appName={appName} testId="reset-password-form">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <h2 className="font-heading text-xl font-semibold">Set New Password</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter your new password below
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">New Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className={`${inputCls} pr-11`}
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 -translate-y-1/2 tap inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            className={inputCls}
            data-testid="reset-confirm-password-input"
          />
        </div>

        <p className="text-xs text-muted-foreground">
          Password must be at least 8 characters long.
        </p>

        <Button
          type="submit"
          className="w-full h-11 font-semibold shadow-glow"
          disabled={isLoading}
          data-testid="reset-password-submit-button"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <KeyRound className="h-4 w-4" />
              Update Password
            </>
          )}
        </Button>
      </form>
    </AuthFrame>
  );
}
